import { useState, useEffect } from 'react';
import {
  IconChevronRightFilled,
  IconChevronLeft,
  IconBasketFilled,
} from '@tabler/icons-react';
import CategoryButton from '../components/CategoryButton';
import TarjetaPOS from '../components/TarjetaPOS';
import AddedCart from '../components/AddedCart';
import EraseButtonRead from '../components/EraseButtonRead';
import EmptyState from '../components/EmptyState';
import PersonalizarItem from '../components/PersonalizarItem';
import ModalCobrarPOS from '../components/ModalCobrarPOS';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';
import TipBlue from '../Utils/TipBlue';

const FONT = '"Public Sans", sans-serif';
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const PRODUCT_GRID = { gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' };
const SUMMARY_SURFACE = {
  backgroundColor: 'rgba(20, 20, 20, 0.82)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 20px 48px rgba(0, 0, 0, 0.55)',
};

function formatPrice(price) {
  return (Number(price) || 0).toFixed(2);
}

function CategoryRail({ categories, activeId, onSelect }) {
  if (!categories.length) return null;

  return (
    <div
      role="tablist"
      aria-label="Categorías"
      className="grid gap-2 mb-3 w-full min-w-0 shrink-0"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 148px), 1fr))' }}
    >
      {categories.map((cat) => (
        <CategoryButton
          key={cat.id}
          name={cat.name}
          icon={cat.icon}
          color={cat.color}
          fill
          active={activeId === cat.id}
          onClick={() => onSelect(cat.id)}
        />
      ))}
    </div>
  );
}

function ProductSkeleton() {
  return (
    <div
      className="grid gap-3 flex-1 min-w-0 min-h-0 overflow-hidden content-start"
      style={{ ...PRODUCT_GRID, gridAutoRows: '178px' }}
      role="status"
      aria-label="Cargando productos"
    >
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div
          key={i}
          className="rounded-2xl animate-pulse"
          style={{ backgroundColor: '#141414' }}
        />
      ))}
    </div>
  );
}

export default function POS({ onOrderSuccess, notify }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [nextCartId, setNextCartId] = useState(1);
  const [facturacion, setFacturacion] = useState(false);
  const [showAside, setShowAside] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalItem, setModalItem] = useState(null);

  const todayKey = DAY_KEYS[new Date().getDay()];

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [cRes, pRes] = await Promise.all([
          apiFetch(`/categories?day=${todayKey}`),
          apiFetch(`/products?day=${todayKey}`),
        ]);
        if (!cRes.ok) throw new Error(`HTTP ${cRes.status}`);
        if (!pRes.ok) throw new Error(`HTTP ${pRes.status}`);
        const cats = await cRes.json();
        const prods = await pRes.json();
        if (cancelled) return;
        setCategories(cats);
        setProducts(prods);
        setActiveCategory((prev) => prev ?? cats[0]?.id ?? null);
      } catch (e) {
        console.error('Error al cargar productos:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [todayKey]);

  const filtered = products.filter(
    (p) =>
      p.is_available &&
      (p.category_id === activeCategory ||
        (p.categories || []).some((c) => c.id === activeCategory))
  );

  const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
  const ivaIncluded = facturacion ? subtotal - subtotal / 1.16 : 0;
  const total = subtotal;

  const openVariantModal = (product, cartId) => {
    setModalItem({ product, cartId });
  };

  const handleConfirmItem = ({ optionIds, variants, quantity }) => {
    const { product, cartId } = modalItem;
    const basePrice = Number(product.price) || 0;
    const variantsCost = (variants || []).reduce((sum, v) => sum + (Number(v.price_delta) || 0), 0);
    const unitPrice = basePrice + variantsCost;
    const entry = {
      item_id: product.id,
      name: product.name,
      image: product.img_url || '',
      basePrice,
      quantity,
      variants: variants || [],
      variant_option_ids: optionIds,
      unitPrice,
      price: unitPrice * quantity,
    };
    if (cartId == null) {
      setCart((prev) => [...prev, { ...entry, cartId: nextCartId }]);
      setNextCartId((prev) => prev + 1);
    } else {
      setCart((prev) => prev.map((item) => (item.cartId === cartId ? { ...item, ...entry } : item)));
    }
    setModalItem(null);
    notify?.('success', cartId == null ? 'Artículo agregado correctamente' : 'Artículo actualizado correctamente');
  };

  const removeFromCart = (cartId) => {
    setCart((prev) => prev.filter((item) => item.cartId !== cartId));
  };

  const handleCloseOrder = async ({ paymentMethod, orderType, customerId, cashReceived, cashChange, promo }) => {
    if (!cart.length) return;
    setSubmitting(true);
    const startedAt = Date.now();
    notify?.('loading', 'Procesando pedido...');
    let ok = false;
    try {
      const createRes = await apiFetch(`/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sale_type: orderType,
          customer_id: customerId ?? null,
          notes: null,
          created_by: null,
          requires_invoice: facturacion,
        }),
      });
      if (!createRes.ok) throw new Error(`HTTP ${createRes.status}`);
      const sale = await createRes.json();
      const saleId = sale.id;

      for (const item of cart) {
        const itemRes = await apiFetch(`/sales/${saleId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            item_id: item.item_id,
            quantity: item.quantity,
            variant_option_ids: item.variant_option_ids && item.variant_option_ids.length ? item.variant_option_ids : null,
          }),
        });
        if (!itemRes.ok) throw new Error(`HTTP ${itemRes.status}`);
      }

      const payRes = await apiFetch(`/sales/${saleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: paymentMethod,
        }),
      });
      if (!payRes.ok) throw new Error(`HTTP ${payRes.status}`);

      const recalcRes = await apiFetch(`/sales/${saleId}/recalculate`, {
        method: 'POST',
      });
      if (!recalcRes.ok) throw new Error(`HTTP ${recalcRes.status}`);
      let recalculated = await recalcRes.json();

      // Una sola promoción por orden: se aplica al final y recalcula totales
      if (promo) {
        const promoRes = await apiFetch(`/sales/${saleId}/apply-discount`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: promo.source, discount_id: promo.id }),
        });
        if (!promoRes.ok) throw new Error(`HTTP ${promoRes.status}`);
        recalculated = await promoRes.json();
      }

      if (customerId) {
        const pointsRes = await apiFetch(`/points-config`);
        if (!pointsRes.ok) throw new Error(`HTTP ${pointsRes.status}`);
        const config = await pointsRes.json();
        const pesosPerPoint = Number(config.pesos_per_point) || 10;
        const earned = Math.floor(Number(recalculated.total_amount) / pesosPerPoint);
        if (earned > 0) {
          await apiFetch(`/customers/${customerId}/points`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              movement_type: 'earned',
              points: earned,
              sale_id: saleId,
              notes: 'Puntos por compra en punto de venta',
            }),
          });
        }
      }

      ok = true;
    } catch (e) {
      console.error('Error al cerrar la orden:', e);
    }
    await ensureMinLoading(startedAt);
    notify?.('clear');
    if (ok) {
      setCart([]);
      setFacturacion(false);
      onOrderSuccess?.();
      notify?.('success', 'Pedido cobrado correctamente');
    } else {
      notify?.('error', 'No se pudo cobrar el pedido. Si el problema persiste, reporta el error.');
    }
    setSubmitting(false);
  };

  const editingItem =
    modalItem?.cartId != null ? cart.find((it) => it.cartId === modalItem.cartId) : null;

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16] flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
            }}
          >
            Punto de venta
          </h1>
          <p
            className="m-0 mt-1"
            style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}
          >
            Levanta pedidos mucho más rápido
          </p>
        </div>
        <TipBlue openUp={false} alignRight={true}>
          Selecciona productos, personaliza variantes y cobra desde el carrito lateral.
        </TipBlue>
      </header>

      <div className="flex flex-1 min-w-0 min-h-0">
        <main className="flex flex-col flex-1 min-w-0 min-h-0 px-4 lg:px-6 pt-5 lg:pt-6 pb-4">
          {modalItem ? (
            <PersonalizarItem
              product={modalItem.product}
              initialSelection={editingItem?.variants || []}
              isEditing={!!editingItem}
              onBack={() => setModalItem(null)}
              onConfirm={handleConfirmItem}
            />
          ) : (
            <>
              <CategoryRail
                categories={categories}
                activeId={activeCategory}
                onSelect={setActiveCategory}
              />

              {loading ? (
                <ProductSkeleton />
              ) : filtered.length === 0 ? (
                <div className="flex-1 min-h-0">
                  <EmptyState
                    icon={IconBasketFilled}
                    title="Sin productos"
                    description="No hay productos disponibles en esta categoría. Agrega productos para que tu menú no quede en el vacío."
                    className="h-full"
                  />
                </div>
              ) : (
                <div
                  className="grid gap-3 flex-1 min-w-0 min-h-0 overflow-y-auto content-start pedidos-scroll pb-1"
                  style={{ ...PRODUCT_GRID, gridAutoRows: '178px' }}
                >
                  {filtered.map((product) => (
                    <TarjetaPOS
                      key={product.id}
                      name={product.name}
                      price={formatPrice(product.price)}
                      image={product.img_url || ''}
                      onAdd={() => openVariantModal(product, null)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </main>

        <div className="relative flex h-full shrink-0">
          <button
            type="button"
            onClick={() => setShowAside(!showAside)}
            aria-label={showAside ? 'Ocultar artículos' : 'Mostrar artículos'}
            className="absolute z-10 flex items-center justify-center w-8 h-16 rounded-full border-none cursor-pointer transition-colors duration-200 ease-out active:scale-[0.96] hover:bg-white/[0.14]"
            style={{
              left: showAside ? 0 : -36,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            {showAside ? (
              <IconChevronRightFilled size={15} color="rgba(255, 255, 255, 0.75)" />
            ) : (
              <IconChevronLeft size={16} color="rgba(255, 255, 255, 0.75)" />
            )}
          </button>

          {showAside && (
            <aside className="flex flex-col h-full overflow-hidden w-[300px] lg:w-[330px] xl:w-[380px] mr-3 lg:mr-4 pl-5 lg:pl-6 pt-5 lg:pt-6 border-l border-white/[0.16]">
              <div
                className="flex items-center justify-between gap-3 shrink-0 pb-4"
                style={{ minHeight: '44px' }}
              >
                <div className="flex items-baseline gap-2 min-w-0">
                  <h2
                    className="font-semibold m-0 truncate"
                    style={{
                      fontSize: '20px',
                      lineHeight: '26px',
                      letterSpacing: '-0.01em',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  >
                    Artículos
                  </h2>
                  {cart.length > 0 && (
                    <span
                      className="shrink-0"
                      style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.4)', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {cart.length}
                    </span>
                  )}
                </div>
                {cart.length > 0 && <EraseButtonRead onClick={() => setCart([])} />}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll pb-3">
                {cart.length === 0 ? (
                  <EmptyState
                    icon={IconBasketFilled}
                    title="Sin artículos"
                    description="Agrega productos para comenzar la orden."
                    className="h-full"
                  />
                ) : (
                  <div className="flex flex-col gap-3">
                    {cart.map((item) => (
                      <AddedCart
                        key={item.cartId}
                        name={item.name}
                        price={formatPrice(item.price)}
                        image={item.image}
                        variants={item.variants || []}
                        quantity={item.quantity || 1}
                        onModify={() =>
                          openVariantModal(
                            { id: item.item_id, name: item.name, price: item.basePrice, img_url: item.image },
                            item.cartId
                          )
                        }
                        onDelete={() => removeFromCart(item.cartId)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="shrink-0 pt-4 pb-4 border-t border-white/[0.16]">
                <div className="flex flex-col gap-4 rounded-3xl p-4" style={SUMMARY_SURFACE}>
                  <div className="flex flex-col gap-2" style={{ fontFamily: FONT, fontSize: '14px' }}>
                    <div className="flex items-center justify-between">
                      <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}>Subtotal</span>
                      <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.7)', fontVariantNumeric: 'tabular-nums' }}>
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>
                    {facturacion && (
                      <div className="flex items-center justify-between">
                        <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}>IVA (16%)</span>
                        <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.7)', fontVariantNumeric: 'tabular-nums' }}>
                          ${ivaIncluded.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-baseline justify-between pt-1">
                      <span style={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.55)' }}>Total</span>
                      <span
                        style={{
                          fontSize: '26px',
                          lineHeight: '32px',
                          fontWeight: 600,
                          letterSpacing: '-0.02em',
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ${total.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.55)', fontFamily: FONT }}>
                      Facturación
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={facturacion}
                      aria-label="Facturación"
                      onClick={() => setFacturacion(!facturacion)}
                      className="relative flex items-center shrink-0 rounded-full border-none cursor-pointer transition-colors duration-200 ease-out"
                      style={{
                        width: '50px',
                        height: '30px',
                        margin: '7px 0',
                        backgroundColor: facturacion ? '#0071e3' : 'rgba(255, 255, 255, 0.12)',
                      }}
                    >
                      <span
                        className="absolute rounded-full bg-white transition-all duration-200 ease-out"
                        style={{
                          width: '24px',
                          height: '24px',
                          top: '3px',
                          left: facturacion ? '23px' : '3px',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        }}
                      />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    disabled={!cart.length}
                    className="w-full rounded-full border-none font-semibold transition-all duration-200 ease-out active:scale-[0.98] hover:brightness-110"
                    style={{
                      height: '52px',
                      backgroundColor: '#0071e3',
                      color: '#fff',
                      fontSize: '16px',
                      letterSpacing: '-0.01em',
                      fontFamily: FONT,
                      opacity: cart.length ? 1 : 0.35,
                      cursor: cart.length ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Pagar
                  </button>
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>

      {showModal && (
        <ModalCobrarPOS
          isOpen={showModal}
          itemCount={cart.length}
          subtotal={subtotal}
          ivaIncluded={ivaIncluded}
          total={total}
          cartItems={cart}
          facturacion={facturacion}
          submitting={submitting}
          onClose={() => setShowModal(false)}
          onConfirm={handleCloseOrder}
        />
      )}
    </div>
  );
}
